// Round 4e (47099): the shell's badge counts and display-only boot reads as ONE request (GET /api/shell/badges?parts=..) instead of one each.
// read(part) answers what the part's own route answered (resolves its body; rejects on its non-200 like api.request), so every badge store keeps
// its own logic. Every read asked before the flush shares one request; the flush runs on `schedule` (the app passes afterPaint: after first
// paint and once the page's own reads are quiet; a flush in flight keeps the network busy, so the next one waits for it). One poll timer
// re-reads the polled parts together (60 s, paused while hidden, no overlap). An API without the route (404) -> the old separate calls.
import { makeBadgePoller } from './invoiceCount.logic.mjs';

export const PART_PATHS = {
  to_invoice: '/sales-invoices/to-invoice/count', dispatches: '/sales-dispatches/count', planned: '/planned-transactions/count',
  review: '/review/pending/count', attention: '/payments/attention', data_health: '/data-health/count', refresh_health: '/admin/refresh-jobs/summary', alert_bands: '/alerts/live-state',
  price_alerts: '/inventory/price-alerts?firing_only=true', feed_runout: '/inventory/feed-runout', credit_over: '/sales/credit-alerts?include=over',
  sanity_summary: '/sanity/family-summary', period_windows: '/period-lock?view=windows', help: '/help', version: '/version',
};
const partError = (part, r) => Object.assign(new Error(`shell part ${part}: ${r ? r.status : 'missing'}`), { status: r ? r.status : 0, body: r && r.body, code: (r && r.body && r.body.code) || null });

export function makeShellReader({ request, schedule, hasToken = () => true, pollOpts = {} }) {
  let pending = new Map(), fresh = false, scheduled = false, legacy = false;   // part -> [{ resolve, reject }]
  const legacyRead = (part, f) => request(PART_PATHS[part] + (f && part === 'dispatches' ? '?fresh=1' : ''));
  async function flush() {
    scheduled = false;
    const batch = pending, f = fresh; pending = new Map(); fresh = false;
    const settle = (part, p) => p.then(b => batch.get(part).forEach(w => w.resolve(b)), e => batch.get(part).forEach(w => w.reject(e)));
    if (legacy) { batch.forEach((_, part) => settle(part, legacyRead(part, f))); return; }
    let body;
    try { body = await request(`/shell/badges?parts=${[...batch.keys()].join(',')}${f ? '&fresh=1' : ''}`, { keepAlive: true }); } catch (e) {
      if (e && e.status === 404) { legacy = true; batch.forEach((_, part) => settle(part, legacyRead(part, f))); return; }   // API without the route
      batch.forEach(ws => ws.forEach(w => w.reject(e))); return;
    }
    batch.forEach((ws, part) => {
      const r = body && body[part];
      if (r && r.status === 404 && r.body && r.body.code === 'SHELL_PART_UNKNOWN') return settle(part, legacyRead(part, f));   // an API older than this web: that part the old way
      ws.forEach(w => (r && r.status === 200 ? w.resolve(r.body) : w.reject(partError(part, r))));
    });
  }
  function read(part, { fresh: f = false } = {}) {
    if (!PART_PATHS[part]) return Promise.reject(new Error(`unknown shell part ${part}`));
    return new Promise((resolve, reject) => {
      if (!pending.has(part)) pending.set(part, []);
      pending.get(part).push({ resolve, reject });
      if (f) fresh = true;
      if (!scheduled) { scheduled = true; schedule(flush); }
    });
  }
  // poll(part, cb): cb(body) / cb(null, err) now and on every tick while subscribed; returns unsubscribe. One timer for every polled part.
  const polled = new Map();   // part -> Set(cb)
  const fan = (part, b, e) => (polled.get(part) || []).forEach(cb => cb(e ? null : b, e));
  const readInto = (part) => read(part).then(b => fan(part, b), e => fan(part, null, e));
  const poller = makeBadgePoller(() => Promise.all([...polled.keys()].map(readInto)), { hasToken, ...pollOpts });
  let stop = null;
  function poll(part, cb) {
    const first = !polled.size;
    if (!polled.has(part)) polled.set(part, new Set());
    const fresh1 = !polled.get(part).size;
    polled.get(part).add(cb);
    if (first) stop = poller.start();
    // a part joining reads now, like its own poller did (also when first: start()'s tick is skipped while a stopped poll's tick is still in flight)
    if (fresh1) read(part).then(b => cb(b), e => cb(null, e));
    return () => {
      const s = polled.get(part); if (!s) return;
      s.delete(cb); if (!s.size) polled.delete(part);
      if (!polled.size && stop) { stop(); stop = null; }
    };
  }
  return { read, poll };
}
